import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/api/client.types';
import { courseQueryKey } from '@/features/courses/useCourse';

import type { QuestionClassActionParams, SetQuestionRemovedParams } from './gradebookService';
import { useKuduGradebookService } from './gradebookService';
import { studentGradesQueryKeyPrefix } from './useStudentGrades';

function unavailableError(): ApiError {
  const error = new Error('Unable to change the assignment right now.') as ApiError;
  error.kind = 'data';
  return error;
}

/**
 * Gives every student on the roster full credit for one question through
 * GiveQuestionFullCredit, then reloads the class's grades. Nothing is updated
 * optimistically: the change reaches every student, so the page shows what the
 * server stored.
 */
export function useGiveQuestionFullCredit(courseId: string | undefined) {
  const queryClient = useQueryClient();
  const service = useKuduGradebookService();

  return useMutation<void, ApiError, QuestionClassActionParams>({
    mutationFn: async (params) => {
      if (!service || !courseId) {
        throw unavailableError();
      }
      await service.giveQuestionFullCredit(params);
    },
    onSuccess: async () => {
      if (courseId) {
        await queryClient.invalidateQueries({ queryKey: studentGradesQueryKeyPrefix(courseId) });
      }
    },
  });
}

/**
 * Removes a question from an assignment, or restores it, through
 * SetQuestionRemoved. Then it reloads everything that changed: the class's
 * grades (their totals), the course (the gradebook's question lists and
 * maxima) and the chapter.
 */
export function useSetQuestionRemoved(courseId: string | undefined) {
  const queryClient = useQueryClient();
  const service = useKuduGradebookService();

  return useMutation<void, ApiError, SetQuestionRemovedParams>({
    mutationFn: async (params) => {
      if (!service || !courseId) {
        throw unavailableError();
      }
      await service.setQuestionRemoved(params);
    },
    onSuccess: async (_data, params) => {
      if (!courseId) {
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: studentGradesQueryKeyPrefix(courseId) }),
        queryClient.invalidateQueries({ queryKey: courseQueryKey(courseId) }),
        queryClient.invalidateQueries({
          queryKey: ['component', courseId, params.assignment.componentUid],
        }),
      ]);
    },
  });
}
